import { ChangeDetectionStrategy, Component, inject, Input, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, shareReplay, switchMap, take, tap } from 'rxjs';
import { IonicModule } from '@ionic/angular';
import { GameDetailsComponent } from '@ui-components';
import { JoinButtonsComponent } from '../join-buttons/join-buttons.component';
import { GameDetailsQueryHandler } from '../../application/handlers';
import { GameDetailsModel, MODAL_TOKEN, ModalProvider } from '@core';
import { OpenSingleChatCommandHandler } from '@messages';
import { GamesService } from '@games/infra';
import { ChatService } from '@messages/infra';

@Component({
  templateUrl: './game-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, GameDetailsComponent, JoinButtonsComponent],
  providers: [GameDetailsQueryHandler, GamesService, OpenSingleChatCommandHandler, ChatService]
})
export class GameDetailsModalComponent implements OnInit {
  @Input() gameId!: string;
  @Input() adminAccess: boolean = false;

  private readonly gameDetailsQueryHandler: GameDetailsQueryHandler = inject(GameDetailsQueryHandler);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);

  private readonly gameIdSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  readonly gameDetails: Signal<GameDetailsModel | undefined> = toSignal(
    this.gameIdSubject.asObservable().pipe(
      filter((gameId: string) => !!gameId),
      switchMap((gameId: string) => this.gameDetailsQueryHandler.getGameDetails(gameId)),
      shareReplay(1)
    )
  );

  ngOnInit(): void {
    this.gameIdSubject.next(this.gameId);
  }

  sendMessage(payload: { userId: string; displayName: string | undefined }): void {
    this.openSingleChatCommandHandler
      .open(payload.userId, payload.displayName)
      .pipe(
        take(1),
        tap(() => this.modalProvider.dismissAllStoredModals())
      )
      .subscribe();
  }
}
